import { TExports } from "./types"
import "./styles.scss"
import { Avatar, Button, CampaignsFactory, CircularProgress, ContentSectionContainer, DataTable, DialogHandler, EmptyContent, ETaskType, FormModel, FullBar, GenericWrapperContext, getCalendarFormat, Header, Icon, ListsFactory, LoadingContainer, Search, SummaryEnhancedFormModel, SuppressedEmailsFactory, TasksFactory, TDataTableColumnsData, TDataTableRowData, TGenericListReturn, TGenericReturn, TOrder, TPromisePoolPartialUser, TPromisePoolRef, TPromisePoolReturnData, TTask, TTaskDownload, UserModel, UsersFactory } from "@cakemail-org/ui-components-v2"
import { Stack } from "@mui/material"
import { useTranslation } from "react-i18next"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import moment from "moment"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"

export function Exports({ }: TExports) {
    const { t } = useTranslation()
    const genericWrapper = useContext(GenericWrapperContext)

    const [loading, setLoading] = useState(false)
    const [tasks, setTasks] = useState<TGenericListReturn<TTask>>()
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(25)
    const [order, setOrder] = useState<TOrder>("desc")
    const [orderBy, setOrderBy] = useState<keyof TTask>("created_on") // expires_on, status

    const [users, setUsers] = useState<{ [id: string]: UserModel }>()

    const callTimeoutRef = useRef<NodeJS.Timeout>(undefined)

    useEffect(() => {
        fetchTasks()
    }, [page, perPage, order, orderBy])

    useEffect(() => {
        UsersFactory.list({
            page: 1,
            perPage: 1000
        }).then((res) => {
            const curUsers: any = {}
            res.data.forEach((user: UserModel) => {
                curUsers[user.id] = user
            })
            setUsers(curUsers)
        })
    }, [])

    function fetchTasks() {
        setLoading(true)
        clearTimeout(callTimeoutRef.current)
        TasksFactory.list({
            withCount: true,
            page: page,
            perPage: perPage,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
        }).then((data) => {
            const pendingTask = data.data.find((task: TTask) => task.status === "pending")
            if (pendingTask) {
                callTimeoutRef.current = setTimeout(() => {
                    fetchTasks()
                }, 5000)
            }

            setTasks(data)
        }).catch(() => {
            DialogHandler.show({
                type: "error",
                title: t("general-error-action")
            })
        }).finally(() => {
            setLoading(false)
        })
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as keyof TTask)
    }


    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
    }

    function handleDownload(task: TTask, btn: TButtonRef) {
        let service: Function = () => { }
        let params = {}

        const exportId = task?.id
        const listId = task?.payload?.listId
        const campaignId = task?.payload?.campaignId

        switch (task.type) {
            case ETaskType.contacts_export:
                service = ListsFactory.downloadContactsExport
                params = { listId, exportId }
                break
            case ETaskType.campaign_logs_export:
                service = CampaignsFactory.downloadCampaignLogExport
                params = { campaignId, exportId }
                break
            case ETaskType.campaigns_reports_export:
                service = CampaignsFactory.downloadReportExport
                params = { exportId }
                break
            case ETaskType.suppressed_emails_export:
                service = SuppressedEmailsFactory.downloadExport
                params = { exportId }
                break
            case ETaskType.list_logs_export:
                service = ListsFactory.downloadListLogExport
                params = { listId, exportId }
                break
            default:
                console.error("Invalid export type", task.type)
                if (btn) setTimeout(btn.reset, 1000)
                return
        }

        service(params).then((data: TGenericReturn<TTaskDownload>) => {
            btn.reset()
            window.open(data.data.url, "_blank")
        }).catch(() => {
            DialogHandler.show({
                type: "error",
                title: t("general-error-action")
            })
        })
    }

    const colsData: TDataTableColumnsData[] = useMemo(() => {
        return [
            {
                id: "type",
                label: t("general-activity_one")
            },
            {
                id: "details",
                label: t("general-details")
            },
            {
                id: "created_on",
                label: t("general-created_on"),
                sortable: true
            },
            {
                id: "requested_by",
                label: t("general-user_one")
            },
            {
                id: "actions",
                label: ""
            }
        ]
    }, [])


    const rows: any[] = tasks && tasks.data.map((task: TTask, index) => {
        const canDownload = moment.unix(task.expires_on).diff(moment()) > 0
        let actions = undefined
        if (task.status === "pending") {
            actions = <CircularProgress variant={task.progress > 0 ? "determinate" : "indeterminate"} value={task.progress} size={28} />

        } else if (task.status === "ready" && canDownload) {
            actions = <Button
                size="small"
                variant="outlined"
                animated
                onClick={(e, btn) => {
                    handleDownload(task, btn)
                }}
            >
                {t("general-download")}
            </Button>
        }

        let splitText = task?.description?.split(";;")
        let desc = ""
        let detailTxt = ""

        if (task.type === ETaskType.contacts_export) {
            if (splitText?.length === 2) {
                desc = `${splitText[0]} ${t("general-in-txt")} ${splitText[1]}`
            } else {
                if (task?.payload?.jsonFilter) {
                    desc = `${t("general-filtered")} ${t("general-in-txt")} ${task.description}`
                } else {
                    desc = `${t("general-all-contacts")} ${t("app-general-txt-in")} ${task.description}`
                }
            }

            detailTxt = t("general-all-contacts")
        } else if (task.type === ETaskType.campaigns_reports_export) {
            detailTxt = t("general-campaign_other")
        } else if (task.type === ETaskType.suppressed_emails_export) {
            detailTxt = t("general-emails")
        } else {
            desc = splitText?.[0] || ""
            detailTxt = t("general-activity_other")
        }

        return {
            type: t(`tasks-listing-type-${task.type}`),
            details: desc ?
                `${desc}, ${task?.payload?.count} ${detailTxt}` :
                `${task?.payload?.count} ${detailTxt}`,
            created_on: getCalendarFormat({ unixDate: task.created_on }),
            requested_by: <Stack direction="row" alignItems="center" gap={4}>
                <Avatar
                    email={task.requested_by?.email}
                    firstName={users?.[task.requested_by?.id]?.first_name || ""}
                    lastName={users?.[task.requested_by?.id]?.last_name || ""} />
                {users?.[task.requested_by?.id]?.first_name || ""} {users?.[task.requested_by?.id]?.last_name || ""}
            </Stack>,
            actions: actions
        }
    }) || []



    return <ContentSectionContainer className="exportsListingContainer">
        <Stack gap={8}>
            <FullBar
                className={loading ? "loading" : ""}
                left={<Header
                    heading={t("general-export_one")}
                    headingVariant="titleL"
                    support={genericWrapper.account?.name}
                    headingTooltip={t("exports-heading-tooltip")}
                />}
            />

            <LoadingContainer loading={!tasks}>
                {tasks && tasks.data.length === 0 &&
                    <EmptyContent
                        title={t("exports-listing-empty-title")}
                        desc={t("exports-listing-empty-desc")}
                    />
                }

                {tasks && tasks.data.length > 0 &&
                    <DataTable
                        initState={{
                            rowsPerPage: perPage,
                            order,
                            orderBy,
                        }}
                        totalCount={tasks?.pagination?.count}
                        className={`exportsDataTable ${loading ? "loading" : ""}`}
                        onPaginationChange={handlePaginationChange}
                        onSortChange={handleSortChange}
                        rows={rows}
                        columnsData={colsData}
                        stickyLastCol
                    />
                }

            </LoadingContainer>
        </Stack>
    </ContentSectionContainer>
}